/**
 * Plain words for the raw codes that reach the screen (PR 12, tracker E1).
 *
 * Three places printed an internal code as if it were a label: the inventory
 * signal badge ("REDUCE BIDS 30PCT"), the change history ("keyword:
 * 440986567437068"), and the Pending changes tray ("CAMPAIGN_LAUNCH ·
 * LAUNCH_CAMPAIGN"). The words live here, once, so a new surface asks for a
 * label instead of inventing one. Every function is total: an unknown code
 * falls back to the code with underscores read as spaces and a capital first
 * letter, never to a blank and never to a throw.
 *
 * Pure, no React.
 */

/** "reduce_bids_30pct" -> "Reduce bids 30pct": the fallback for a code with no entry. */
function humanise(code: string): string {
  const s = code.replace(/_/g, ' ').trim()
  return s ? `${s.charAt(0).toUpperCase()}${s.slice(1)}` : s
}

// ── Inventory signal (the Signal column and badge) ──────────────────────────

export const INVENTORY_SIGNAL_LABELS: Readonly<Record<string, string>> = {
  hold_bids: 'Hold bids',
  reduce_bids_30pct: 'Reduce bids 30%',
  pause_campaigns: 'Pause campaigns',
  resume_optimization: 'Resume optimizing',
}

export function inventorySignalLabel(code: string): string {
  return INVENTORY_SIGNAL_LABELS[code] ?? humanise(code)
}

/** The filter choices of every Signal column, in the order the engine escalates. */
export const INVENTORY_SIGNAL_OPTIONS: Array<{ value: string; label: string }> =
  Object.keys(INVENTORY_SIGNAL_LABELS).map(value => ({ value, label: INVENTORY_SIGNAL_LABELS[value] }))

// ── What a change was made to ───────────────────────────────────────────────

export const ENTITY_TYPE_LABELS: Readonly<Record<string, string>> = {
  keyword: 'Keyword',
  target: 'Product target',
  campaign: 'Campaign',
  ad_group: 'Ad group',
  product_ad: 'Product ad',
  placement: 'Placement',
  search_term: 'Search term',
  campaign_launch: 'New campaign',
  listing: 'Listing',
  listing_images: 'Listing images',
  harvest: 'Harvest',
}

export function entityTypeLabel(type: string): string {
  return ENTITY_TYPE_LABELS[type] ?? humanise(type)
}

// ── Which setting of it changed ─────────────────────────────────────────────

export const FIELD_LABELS: Readonly<Record<string, string>> = {
  bid: 'Bid',
  budget: 'Daily budget',
  state: 'Status',
  modifier: 'Placement adjustment',
  add_negative: 'Negative',
  launch_campaign: 'Launch',
}

export function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? humanise(field)
}

/** "Keyword · Bid": a change's headline when nothing more specific is known. */
export function changeHeadline(entityType: string, field: string): string {
  return `${entityTypeLabel(entityType)} · ${fieldLabel(field)}`
}

/** One placement vocabulary for every screen (PR 12b, E1). The codes in the wild:
 *  ClickPay's own buckets ('top'|'rest'|'product'), the short names ('tos'|'ros'|'pp'),
 *  the audit trail's 'rest_of' / 'product_page', Amazon's report strings
 *  ('Top of Search on-Amazon') and the Ads API enums ('PLACEMENT_TOP'). */
export const PLACEMENT_LABELS: Readonly<Record<string, string>> = {
  top: 'Top of search', tos: 'Top of search', top_of_search: 'Top of search', placementtop: 'Top of search',
  placement_top: 'Top of search',
  'top of search on-amazon': 'Top of search', 'top of search (first page)': 'Top of search',
  'top of search': 'Top of search',
  rest: 'Rest of search', ros: 'Rest of search', rest_of: 'Rest of search', rest_of_search: 'Rest of search',
  placementrestofsearch: 'Rest of search', placement_rest_of_search: 'Rest of search',
  'other on-amazon': 'Rest of search', 'rest of search': 'Rest of search',
  product: 'Product pages', pp: 'Product pages', product_page: 'Product pages', product_pages: 'Product pages',
  placementproductpage: 'Product pages', placement_product_page: 'Product pages',
  'detail page on-amazon': 'Product pages', 'product pages': 'Product pages',
  off: 'Off Amazon', off_amazon: 'Off Amazon', 'off amazon': 'Off Amazon',
}

/** A placement code in words: unknown codes read as words, blank as a dash. */
export function placementLabel(code: string | null | undefined): string {
  const c = (code ?? '').trim()
  if (!c) return '—'
  return PLACEMENT_LABELS[c.toLowerCase()] ?? humanise(c)
}

/** What a history row says it touched: "Keyword · kids scale", falling back to
 *  the id when the name is unknown ("Keyword · 440986567437068"). */
export function entityRef(type: string, label: string | null | undefined, id: string): string {
  let name = label && label.trim() ? label.trim() : id
  // Placement rows are filed as '<campaign>:top|rest_of|product_page|…': print the bucket in words.
  if (type === 'placement') name = placementLabel(label && label.trim() ? label : id.includes(':') ? id.slice(id.indexOf(':') + 1) : id)
  return `${entityTypeLabel(type)} · ${name}`
}
